import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { KubernetesResourceManifestSummaryResource } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import type { KubernetesDetailsFilter } from "~/areas/tasks/components/Task/Kubernetes/KubernetesDetailsLayout";
import { hasNoFilters } from "~/areas/tasks/components/Task/Kubernetes/KubernetesDetailsLayout";
import type { ManifestNavigationTree } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestNavigationTree";
import { applyFilterToKubernetesResourceManifestSummary } from "~/areas/tasks/components/Task/Kubernetes/Manifests/applyFilterToKubernetesResourceManifestSummary";
import type { KubernetesStepWithStatus } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useKubernetesStepsWithStatus";
import type { FilteredItemCounts, ManifestListItem } from "./ManifestViewerList";

export function useManifestsTreeAndOrderedListItems(
    resources: KubernetesResourceManifestSummaryResource[] | undefined,
    filter: KubernetesDetailsFilter,
    stepsWithStatus: KubernetesStepWithStatus[]
): [ManifestNavigationTree, ManifestListItem[], FilteredItemCounts] {
    const filteredSummaries = useMemo(() => {
        if (!resources) {
            return [];
        }

        //if there are no filters, just return the full array
        if (hasNoFilters(filter)) {
            return resources;
        }

        return resources.filter((krm) => applyFilterToKubernetesResourceManifestSummary(krm, filter));
    }, [filter, resources]);

    const navigationTree: ManifestNavigationTree = useMemo(() => {
        if (filteredSummaries.length === 0) {
            return { stepItems: [] };
        }

        //get the unique machine ids
        const machineIds = [...new Set(filteredSummaries.map((krm) => krm.MachineId))];
        //get all the unique namespaces
        const namespaces = [...new Set(filteredSummaries.map((krm) => krm.KubernetesObjectNamespace))];

        return {
            stepItems: stepsWithStatus
                .map((step) => {
                    const stepSummaries = filteredSummaries.filter((v) => v.DeploymentActionId === step.actionId);

                    return {
                        stepName: step.stepName,
                        stepStatus: step.status,
                        actionId: step.actionId,
                        targetItems: machineIds
                            .map((machineId) => {
                                const perMachineResources = stepSummaries.filter((krm) => krm.MachineId === machineId);

                                return {
                                    machineId,
                                    targetName: perMachineResources[0]?.MachineName || "", //all the machines will have the same name, so just pick the first one
                                    namespaceItems: namespaces
                                        .map((ns) => {
                                            const perNamespaceResources = perMachineResources.filter((krm) => krm.KubernetesObjectNamespace === ns);

                                            return {
                                                namespace: !ns || StringHelper.isNullOrWhiteSpace(ns) ? "Cluster-scoped" : ns,
                                                manifestItems: perNamespaceResources.map((krm) => ({
                                                    resource: krm,
                                                    name: krm.KubernetesObjectName,
                                                    kind: krm.KubernetesResourceKind,
                                                    objectStatus: krm.ResourceStatus,
                                                })),
                                            };
                                        })
                                        .filter((t) => t.manifestItems.length > 0),
                                };
                            })
                            .filter((t) => t.namespaceItems.length > 0),
                    };
                })
                .filter((t) => t.targetItems.length > 0),
        };
    }, [filteredSummaries, stepsWithStatus]);

    const orderedManifests: ManifestListItem[] = useMemo(() => {
        if (navigationTree.stepItems.length === 0) {
            return [];
        }

        return navigationTree.stepItems.flatMap((stepItem) =>
            stepItem.targetItems
                .flatMap((targetItem) => targetItem.namespaceItems.flatMap((namespaceItem) => namespaceItem.manifestItems))
                .map((manifestItem) => ({
                    key: manifestItem.resource.Id,
                    krm: manifestItem.resource,
                    resourceStatus: manifestItem.resource.ResourceStatus,
                    stepStatus: stepItem.stepStatus,
                }))
        );
    }, [navigationTree]);

    const manifestCounts: FilteredItemCounts = { total: resources?.length || 0, filteredTotal: filteredSummaries.length };

    return [navigationTree, orderedManifests, manifestCounts];
}
